import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AdminContactServices } from "../../services/admin/AdminContactServices";
import { Filter, ArrowRight, Loader2, Inbox } from "lucide-react";
import { formatRelativeTime } from "../../utils/dateUtils";

export default function ContactsPage() {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchMessages = async (currentPage = 1, filter = "") => {
        try {
            setLoading(true);
            const res = await AdminContactServices.getContacts(currentPage, 20, filter);
            if (res.status === "success") {
                setMessages(res.data.messages);
                setTotalPages(res.data.pages);
                setPage(res.data.current_page);
            }
        } catch (error) {
            console.error("Failed to fetch messages:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMessages(1, statusFilter);
    }, [statusFilter]);

    const handleNextPage = () => {
        if (page < totalPages) fetchMessages(page + 1, statusFilter);
    };

    const handlePrevPage = () => {
        if (page > 1) fetchMessages(page - 1, statusFilter);
    };

    const getStatusBadge = (status) => {
        switch (status.toLowerCase()) {
            case "unread":
                return <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Unread</span>;
            case "read":
                return <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Read</span>;
            case "replied":
                return <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Replied</span>;
            default:
                return <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">{status}</span>;
        }
    };

    return (
        <div className="p-6 lg:p-10 max-w-7xl mx-auto min-h-screen">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-normal text-black dark:text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>Contact Messages</h1>
                    <p className="text-sm text-black dark:text-white/50 mt-1">View and reply to inquiries from the website.</p>
                </div>

                <div className="flex gap-4 w-full md:w-auto">
                    <div className="relative flex-1 md:w-64">
                        <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-black/70 dark:text-white/40" size={16} />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1A1A1A] text-sm font-medium focus:border-jazz-brown outline-none appearance-none"
                        >
                            <option value="">All Statuses</option>
                            <option value="unread">Unread</option>
                            <option value="read">Read</option>
                            <option value="replied">Replied</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-black/5 dark:border-white/5 shadow-sm overflow-hidden flex flex-col min-h-[500px]">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-black/5 dark:border-white/5 bg-[#FAFAFA] dark:bg-[#222]">
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50">From</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50">Subject</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50">Date</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50 text-center">Status</th>
                                <th className="px-6 py-4 text-xs font-bold uppercase tracking-widest text-black dark:text-white/50 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5 dark:divide-white/5">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center">
                                        <Loader2 size={32} className="mx-auto animate-spin text-jazz-brown mb-4" />
                                        <p className="text-sm font-bold tracking-widest uppercase text-black dark:text-white/50">Loading Messages...</p>
                                    </td>
                                </tr>
                            ) : messages.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-16 text-center">
                                        <div className="w-16 h-16 bg-black/5 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Inbox size={24} className="text-black/40 dark:text-white/30" />
                                        </div>
                                        <p className="text-sm font-bold tracking-widest uppercase text-black dark:text-white/50 mb-2">No Messages Found</p>
                                    </td>
                                </tr>
                            ) : (
                                messages.map(msg => (
                                    <tr key={msg.id} className={`hover:bg-[#FAFAFA] dark:hover:bg-[#222] transition-colors group ${msg.status === 'unread' ? 'bg-jazz-brown/[0.02]' : ''}`}>
                                        <td className="px-6 py-4">
                                            <p className={`text-sm ${msg.status === 'unread' ? 'font-bold' : 'font-medium'} text-black dark:text-white`}>{msg.name}</p>
                                            <p className="text-xs text-black dark:text-white/50">{msg.email}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className={`text-sm ${msg.status === 'unread' ? 'font-bold' : ''} text-black dark:text-white truncate max-w-xs`}>{msg.subject || "(No Subject)"}</p>
                                        </td>
                                        <td className="px-6 py-4">
                                            <p className="text-xs text-black dark:text-white/50">{formatRelativeTime(msg.created_at)}</p>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {getStatusBadge(msg.status)}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Link
                                                to={`/admin/contacts/${msg.id}`}
                                                className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-black/5 hover:bg-jazz-brown hover:text-white text-black transition-colors"
                                            >
                                                <ArrowRight size={18} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-auto p-4 border-t border-black/5 flex items-center justify-between text-sm">
                        <span className="text-black dark:text-white/60 font-medium">Page {page} of {totalPages}</span>
                        <div className="flex gap-2">
                            <button
                                onClick={handlePrevPage}
                                disabled={page === 1}
                                className="px-4 py-2 border border-black/10 dark:border-white/10 rounded-lg hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition disabled:opacity-50 disabled:cursor-not-allowed font-bold uppercase tracking-wider text-xs text-black dark:text-white"
                            >
                                Prev
                            </button>
                            <button
                                onClick={handleNextPage}
                                disabled={page === totalPages}
                                className="px-4 py-2 border border-black/10 dark:border-white/10 rounded-lg hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition disabled:opacity-50 disabled:cursor-not-allowed font-bold uppercase tracking-wider text-xs text-black dark:text-white"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
